import IntersectionObsManager from "../managers/IntersectionObserver.js";
import { onReducedMotionChange, prefersReducedMotion } from "../helpers/reducedMotion.js";

class VideoSection {
    constructor(elem) {
        this.elem = elem;
        this.player = this.elem.querySelector(".video__player");
        this.cta = this.elem.querySelector(".video__cta");

        this.teaserSrc = this.player.getAttribute("src");
        this.promoSrc = this.player.dataset.promoSrc;

        this.isPromo = false;
        this.isVisible = false;

        this.init();
    }

    // Both sources share one <video>, so a swap needs load() before play() —
    // without it the element keeps decoding the old source and currentTime
    // stays wherever the last one stopped.
    setSource(src, { muted, loop, controls }) {
        this.player.src = src;
        this.player.muted = muted;
        this.player.loop = loop;
        this.player.controls = controls;

        // Safari keeps <video controls> out of the tab order, so focus() would
        // no-op there and hiding the CTA would drop focus to <body>. An explicit
        // tabindex makes the player focusable everywhere, and it comes back off
        // with the controls so the ambient loop is never a tab stop.
        if (controls) {
            this.player.setAttribute("tabindex", "0");
        } else {
            this.player.removeAttribute("tabindex");
        }

        this.player.load();
    }

    // play() rejects whenever the browser declines (Low Power Mode, autoplay
    // policy, a gesture it didn't credit). The ambient loop can swallow that —
    // the poster still describes the state. A rejected promo cannot: playPromo
    // has already hidden the CTA, so nothing on screen would offer a way back.
    play(onReject) {
        const started = this.player.play();

        if (started) started.catch(onReject || (() => {}));
    }

    playTeaser = () => {
        if (prefersReducedMotion() || !this.isVisible) return;

        this.play();
    };

    playPromo = () => {
        this.isPromo = true;
        this.elem.classList.add("is-playing");
        this.setSource(this.promoSrc, { muted: false, loop: false, controls: true });
        // is-playing takes the CTA out of the layout, so move focus onto the
        // native controls before it vanishes from under the keyboard.
        this.player.focus();
        this.play(this.restoreTeaser);
    };

    restoreTeaser = () => {
        this.isPromo = false;
        this.elem.classList.remove("is-playing");
        this.setSource(this.teaserSrc, { muted: true, loop: true, controls: false });
        this.playTeaser();
    };

    // Only the ambient loop pauses offscreen. A promo the visitor started on
    // purpose keeps going — they have the controls to stop it themselves.
    handleIntersection = (entry) => {
        this.isVisible = entry.isIntersecting;

        if (this.isPromo) return;

        if (this.isVisible) {
            this.playTeaser();
        } else {
            this.player.pause();
        }
    };

    // Two-way: turning the preference back off resumes the loop immediately
    // rather than waiting for the next intersection change, which on a section
    // already in view might never come.
    handleMotionPreference = () => {
        if (this.isPromo) return;

        if (prefersReducedMotion()) {
            this.player.pause();
        } else {
            this.playTeaser();
        }
    };

    // A promo that fails to load would otherwise leave a dead black frame with
    // the CTA already hidden. Falling back to the loop restores the affordance.
    // isPromo is false by then, so a failing teaser can't recurse.
    handleError = () => {
        if (this.isPromo) this.restoreTeaser();
    };

    init() {
        this.cta.addEventListener("click", this.playPromo);
        this.player.addEventListener("ended", this.restoreTeaser);
        this.player.addEventListener("error", this.handleError);
        onReducedMotionChange(this.handleMotionPreference);

        this.observer = new IntersectionObsManager({
            rootMargin: "0% 0% 0% 0%",
            threshold: 0,
        });

        this.observer.observe(this.elem, this.handleIntersection);
    }
}

const videoElem = document.querySelector(".video");
if (videoElem) new VideoSection(videoElem);
